import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { ClipboardList, Copy, KeyRound } from "lucide-react";

const errText = (e) => { const d = e?.response?.data?.detail; return typeof d === "string" ? d : d?.message || "Something went wrong."; };
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-CA", { dateStyle: "medium", timeStyle: "short" }) : "—");
const TABS = [["incomplete", "Incomplete"], ["abandoned", "Abandoned"], ["in_progress", "Filling now"], ["submitted", "Submitted"], ["all", "All"]];
const STATE_STYLE = { abandoned: "bg-amber-100 text-amber-800", in_progress: "bg-cyan-100 text-cyan-800", submitted: "bg-emerald-100 text-emerald-800" };
const ENDPOINT = `${process.env.REACT_APP_BACKEND_URL}/api/integrations/form-drafts`;
const btn = "text-sm px-3 py-1.5 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 transition-colors disabled:opacity-50";

function ApiKeyPanel() {
  const [k, setK] = useState(null);
  const [fresh, setFresh] = useState(null);
  const [err, setErr] = useState(null);
  const load = useCallback(() => api.get("/admin/form-drafts/api-key").then((r) => setK(r.data)).catch((e) => setErr(errText(e))), []);
  useEffect(() => { load(); }, [load]);
  const act = async (fn, confirmText) => {
    if (confirmText && !window.confirm(confirmText)) return;
    setErr(null);
    try { const r = await fn(); setFresh(r.data.key || null); setK(r.data); } catch (e) { setErr(errText(e)); }
  };
  const create = () => act(() => api.post("/admin/form-drafts/api-key"), k?.active ? "Replace the key? The old key stops working immediately." : null);
  const revoke = () => act(() => api.delete("/admin/form-drafts/api-key"), "Turn off the key? 25031988.com can no longer fetch until you create a new one.");
  return (
    <section className="pf-card p-5 space-y-3 text-sm" data-testid="drafts-api-panel">
      <h3 className="font-medium text-slate-900 flex items-center gap-2"><KeyRound className="w-4 h-4" /> Fetch API for 25031988.com</h3>
      <p className="text-slate-600">One call: <code className="break-all" data-testid="drafts-api-endpoint">GET {ENDPOINT}?state=abandoned</code> with the header <code>X-Api-Key</code>.</p>
      {k && <p data-testid="drafts-api-status">{k.active ? `Key ${k.prefix}… active since ${fmt(k.created_at)} · last used ${fmt(k.last_used_at)}` : "No key yet. The API refuses every request until you create one."}</p>}
      {fresh && (
        <div className="rounded-lg bg-amber-50 p-3 space-y-2" data-testid="drafts-api-new-key">
          <p className="text-amber-800 font-medium">Copy this key now. It won't be shown again.</p>
          <div className="flex gap-2 items-center">
            <code className="break-all bg-white rounded px-2 py-1 flex-1" data-testid="drafts-api-key-value">{fresh}</code>
            <button type="button" className={btn} onClick={() => navigator.clipboard?.writeText(fresh)} data-testid="drafts-api-copy"><Copy className="w-4 h-4" /></button>
          </div>
        </div>)}
      <div className="flex gap-2">
        <button type="button" className={btn} onClick={create} data-testid="drafts-api-create">{k?.active ? "Replace key" : "Create key"}</button>
        {k?.active && <button type="button" className={btn} onClick={revoke} data-testid="drafts-api-revoke">Turn off key</button>}
      </div>
      {err && <p className="text-rose-600" data-testid="drafts-api-error">{err}</p>}
    </section>
  );
}

function DraftRow({ d }) {
  const [open, setOpen] = useState(false);
  const f = d.fields || {};
  return (
    <>
      <tr className="border-t hover:bg-slate-50 cursor-pointer" onClick={() => setOpen(!open)} data-testid={`draft-row-${d.draft_key}`}>
        <td className="py-2 pr-3 whitespace-nowrap">{fmt(d.updated_at)}</td>
        <td className="pr-3"><span className={`text-xs px-2 py-0.5 rounded-full ${STATE_STYLE[d.state]}`}>{d.state.replace("_", " ")}</span></td>
        <td className="pr-3">{f.contact_name || "—"}</td>
        <td className="pr-3 break-all">{f.work_email || "—"}</td>
        <td className="pr-3">{f.business_name || "—"}</td>
        <td className="pr-3 whitespace-nowrap">{d.filled_count}/{d.total_fields}</td>
      </tr>
      {open && (
        <tr data-testid={`draft-detail-${d.draft_key}`}><td colSpan={6} className="pb-3">
          <dl className="grid sm:grid-cols-3 gap-2 text-xs bg-slate-50 rounded-lg p-3">
            {Object.entries(f).map(([k, v]) => <div key={k}><dt className="text-slate-500">{k}</dt><dd className="break-words">{Array.isArray(v) ? v.join(", ") : v}</dd></div>)}
            <div><dt className="text-slate-500">Saved versions</dt><dd>{d.version}</dd></div>
            <div><dt className="text-slate-500">Started</dt><dd>{fmt(d.created_at)}</dd></div>
            <div><dt className="text-slate-500">Draft key</dt><dd className="break-all">{d.draft_key}</dd></div>
          </dl>
        </td></tr>)}
    </>
  );
}

export default function IncompleteForms() {
  const [state, setState] = useState("incomplete");
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    api.get("/admin/form-drafts", { params: { state } }).then((r) => { setData(r.data); setErr(null); }).catch((e) => setErr(errText(e)));
  }, [state]);
  return (
    <div className="space-y-4" data-testid="incomplete-forms-page">
      <div>
        <div className="pf-chip pf-chip-cyan"><ClipboardList className="w-3 h-3" /> Demo form</div>
        <h2 className="font-display text-3xl font-medium tracking-tight mt-3 text-slate-900">Incomplete forms</h2>
        <p className="text-sm text-slate-600 mt-1">Every demo form visit is autosaved as one record and kept up to date.
          {data && ` A form counts as abandoned after ${data.abandonment_minutes} minutes without changes; records are deleted after ${data.retention_days} days.`}</p>
        {data && !data.autosave_enabled && <p className="text-sm text-amber-700 mt-2" data-testid="drafts-autosave-off">Autosave is turned off on this site (FORM_AUTOSAVE_ENABLED).</p>}
      </div>
      <ApiKeyPanel />
      <div className="flex flex-wrap gap-2" data-testid="drafts-tabs">
        {TABS.map(([s, label]) => (
          <button type="button" key={s} onClick={() => setState(s)} data-testid={`drafts-tab-${s}`}
            className={`text-sm px-3 py-1.5 rounded-full border transition-colors ${state === s ? "bg-slate-900 text-white border-slate-900" : "border-slate-300 hover:border-slate-900"}`}>
            {label}{data ? ` · ${data.summary[s]}` : ""}
          </button>))}
      </div>
      {err && <p className="text-sm text-rose-600" data-testid="drafts-error">{err}</p>}
      {data && (
        <section className="pf-card p-5 overflow-x-auto">
          {data.items.length === 0 ? <p className="text-sm text-slate-500" data-testid="drafts-empty">No forms here yet.</p> : (
            <table className="w-full text-sm" data-testid="drafts-table">
              <thead><tr className="text-left text-xs text-slate-500"><th className="pb-2">Last change</th><th>State</th><th>Name</th><th>Email</th><th>Business</th><th>Filled</th></tr></thead>
              <tbody>{data.items.map((d) => <DraftRow key={d.draft_key} d={d} />)}</tbody>
            </table>)}
        </section>)}
    </div>
  );
}
